import { jsx as c, jsxs as K } from "react/jsx-runtime";
import { useLink as pe } from "@made-people/grease-link";
import { createEventEmitter as be } from "@made-people/grease-event-emitter";
import { createContext as h, useContext as l, useMemo as f, useRef as g, useEffect as y, useReducer as L, useCallback as me } from "react";
import { z as n } from "zod";
import { moneySchema as v } from "@made-people/grease-money";
import { withZod as G } from "@made-people/react-prop-guard";
import { useSeenInViewport as ve } from "@made-people/react-use-seen-in-viewport";
import { overlayEventEmitter as fe } from "@made-people/blueprint-overlay";
const E = be(), A = h(void 0), Ot = (e) => /* @__PURE__ */ c(A.Provider, { value: e, children: e.children }), Pe = () => {
  const e = l(A);
  return e || { additionalData: void 0 };
}, O = n.object({
  attributeLabel: n.string().describe("The localized label of the attribute"),
  attributeKey: n.string().describe("The key of the attribute"),
  // For some reason, z.unknown() makes the field optional
  value: n.custom((e) => typeof e < "u", "Must be set").describe("The value of the attribute"),
  valueLabel: n.string().describe("The localized label of the value")
}).strict(), R = n.object({
  width: n.number().describe("The image width"),
  height: n.number().describe("The image height")
}), ge = n.object({
  type: n.literal("image"),
  src: n.string().describe("The image source"),
  alt: n.string().describe("The image alt text"),
  dimensions: R.optional(),
  attributes: O.array().optional().describe("The image attributes")
}), ye = n.object({
  type: n.literal("video"),
  src: n.string().describe("The video source"),
  poster: n.string().describe("The video poster"),
  dimensions: R.optional(),
  attributes: O.array().optional().describe("The video attributes")
}), Se = n.discriminatedUnion("type", [
  ge,
  ye
]);
function Ie(e) {
  return e.type === "image";
}
const Te = n.object({
  label: n.string().describe("The badge label"),
  badgeType: n.string().describe("The badge type, just a simple string"),
  backgroundColor: n.string().optional().describe("The badge background color"),
  textColor: n.string().optional().describe("The badge text color"),
  imageSrc: n.string().optional().describe("The badge image source")
}), we = n.object({
  name: n.string().describe("The product name"),
  description: n.string().describe("The product description, can be a long text"),
  shortDescription: n.string().describe("The product short description"),
  media: Se.array().describe("The product media (images, videos, etc)"),
  badges: Te.array(),
  specifications: n.record(
    n.string().describe("The group ID of the specifications"),
    n.array(
      n.object({
        label: n.string(),
        value: n.string(),
        icon: n.custom(() => !0).optional()
      })
    )
  ).optional().describe("The product specifications grouped by groupId"),
  brand: n.string().optional().describe("The brand of the product")
}), Ee = n.object({
  categoryId: n.string(),
  slug: n.string(),
  name: n.string()
}), P = n.object({
  productId: n.string().describe("The product ID (could be a database ID)"),
  productKey: n.string().describe("The product business key or ID, sometimes called 'model ID"),
  slug: n.string().describe("The slug part of a product URL"),
  discontinued: n.boolean().describe("Whether the product is discontinued"),
  content: we.describe("All localizable product content"),
  attributes: O.array().describe(
    "The remaining list of product attributes that don't have a place in the required information above"
  ),
  categories: Ee.array().describe("The product categories")
}), Oe = P, D = n.object({
  base: P,
  siblingProducts: Oe.array()
}), w = n.object({
  quantity: n.number().describe("The stock quantity"),
  inStock: n.boolean().describe("Whether the item is in stock"),
  warehouseId: n.string().describe("The warehouse ID, useful when doing omnichannel features")
}), De = n.object({
  percent: n.number().describe("The discount percentage")
}), _ = n.object({
  price: v.describe("The price of the item"),
  priceWithoutDiscount: v.describe(
    "The price of the item without discount"
  )
}), ke = _.merge(
  n.object({
    periodDays: n.number().describe(
      "The period in days for which this was the lowest price (e.g., 30, 60, 90 days)"
    ).optional(),
    date: n.date().describe("The date when the price was as defined in this object")
  })
), N = _.merge(
  n.object({
    discountInfo: De.optional().describe(
      "The discount information and original price in case of a discount"
    ),
    previouslyDiscountedPrices: ke.array().describe("Sorted list of previously discounted prices"),
    discountButNoCampaign: n.boolean().optional().describe(
      "When true, the product has a discounted price but is not part of an active sale campaign. Strikethrough pricing remains visible but sale indicators are suppressed."
    )
  })
), k = n.object({
  sku: n.string().describe(
    "The SKU (Stock Keeping Unit), aka the identifier used by the warehouse to locate the product"
  ),
  inventory: w.describe(
    "The main  consumer facing stock information"
  ),
  additionalInventory: w.array().describe(
    "Additional inventory information, such as stock in other warehouses or physical stores"
  ),
  priceInfo: N.describe("The price information")
}), U = k.merge(
  D.extend({
    dataModel: n.literal("simple"),
    itemId: n.string().describe("The item ID of this product, used for add to cart requests"),
    ean: n.string().optional().describe("The EAN (European Article Number) barcode"),
    outOfStockItemId: n.string().optional().describe(
      "The id that should be used for sending out of stock events, if not added, itemId will be used"
    ),
    isBundle: n.boolean().describe("Whether this product is a bundle")
  })
), Ve = n.object({
  /**
   * The dimension key
   *
   * @example "color" | "size"
   */
  dimensionKey: n.string(),
  /**
   * The translated label or "name" of the dimension
   *
   * @example "Color" | "Färg" | "Size" | "Storlek"
   */
  dimensionLabel: n.string(),
  /**
   * The value of the dimension
   *
   * @example "blue" | "red" | "small" | "large"
   */
  value: n.string(),
  /**
   * The translated label of the dimension value
   *
   * @example "Blue" | "Röd" | "Small" | "Liten"
   */
  valueLabel: n.string()
}), xe = k.extend({
  mainProductId: n.string().describe("The main product ID"),
  mainProductKey: n.string().describe("The main product key"),
  variantId: n.string().describe("The ID of this specific variant"),
  variantKey: n.string().describe("The key of this specific variant"),
  ean: n.string().optional().describe("The EAN (European Article Number) barcode of this variant"),
  outOfStockItemId: n.string().optional().describe(
    "The id that should be used for sending out of stock events, if not added, variantId will be used"
  ),
  label: n.string().describe("The localized label of this variant"),
  dimensions: Ve.array().describe("The dimensions this variant belongs to")
}), $ = D.extend({
  dataModel: n.literal("with_variants"),
  variants: xe.array().describe(
    "The list of purchasable product variants, listed in for instance size pickers.s"
  )
}), Ce = n.object({
  sectionId: n.number(),
  quantity: n.number().describe("The quantity of items in the section"),
  products: n.array(n.union([U, $]))
}), Be = k.extend({
  priceOfItemsOutsideBundle: v.describe(
    "The price of items if bought outside the bundle"
  ),
  discountFromPriceOfItemsOutsideBundle: v.describe(
    "The discount from the price of items if bought outside the bundle"
  ),
  priceMin: v.describe("The minimum price of the bundle"),
  priceMax: v.describe("The maximum price of the bundle")
}), Me = D.extend({
  dataModel: n.literal("bundle"),
  preConfiguredBundle: n.boolean().describe(
    "true if the bundle is preConfigured and can't be changed by the customer"
  ),
  bundlePurchaseInfo: Be,
  itemId: n.string().describe("The item ID of this bundle, used for add to cart requests"),
  sections: Ce.array()
}), z = n.discriminatedUnion("dataModel", [
  U,
  $,
  Me
]), W = n.object({
  product: z,
  relationType: n.string().describe("The type of relation between the products")
}), je = n.object({
  product: z,
  relatedProducts: W.array()
});
function q(e, t) {
  return e.find((r) => Object.entries(t).every(
    ([o, s]) => {
      if (!s)
        return !1;
      const i = r.dimensions.find(
        (d) => d.dimensionKey === o
      );
      return (i == null ? void 0 : i.value) === s.dimensionValue;
    }
  ));
}
function Ke(e, t) {
  var r;
  switch (t.type) {
    case "SET_STATE":
      return { ...e, ...t.payload };
    case "SELECT_OPTION":
      if (!((r = e.optionGroups.find(
        (d) => d.dimensionKey === t.payload.dimensionKey
      )) == null ? void 0 : r.options.some(
        (d) => d.dimensionValue === t.payload.option.dimensionValue
      )))
        return console.error(
          `Option '${t.payload.option.dimensionValue}' does not exist in dimension '${t.payload.dimensionKey}'`
        ), e;
      const s = {
        ...e.selectedOptions,
        [t.payload.dimensionKey]: t.payload.option
      }, i = q(e.variants, s);
      return {
        ...e,
        selectedOptions: s,
        selectedVariant: i || e.selectedVariant
      };
    case "CLEAR_OPTIONS":
      return {
        ...e,
        selectedOptions: {},
        selectedVariant: e.initialProductVariant
      };
    default:
      throw new Error(`Invalid action type '${t == null ? void 0 : t.type}'`);
  }
}
const S = () => {
  const e = l(C);
  if (!e)
    throw new Error(
      "useProductVariant must be used within a ProductVariantProvider"
    );
  return e.selectedVariant;
}, F = () => S().dimensions.map((t) => ({
  dimensionKey: t.dimensionKey,
  dimensionLabel: t.dimensionLabel
})), V = () => {
  const e = l(C);
  if (!e)
    throw new Error(
      "useProductVariantState must be used within a ProductVariantProvider"
    );
  return e;
}, Le = () => {
  const e = l(ee);
  if (!e)
    throw new Error(
      "useProductVariantDispatch must be used within a ProductVariantProvider"
    );
  return e;
};
function Ge(e, t, r) {
  return e.map((o) => {
    const s = Object.entries(r).filter(
      ([i, d]) => i !== o.dimensionKey && d !== void 0
    );
    return s.length === 0 ? o : {
      ...o,
      options: o.options.map((i) => {
        const d = t.some((a) => a.dimensions.some(
          (p) => p.dimensionKey === o.dimensionKey && p.value === i.dimensionValue
        ) ? a.inventory.inStock && s.every(
          ([p, b]) => a.dimensions.some(
            (j) => j.dimensionKey === p && j.value === b.dimensionValue
          )
        ) : !1);
        return {
          ...i,
          inventory: {
            ...i.inventory,
            inStock: d
          }
        };
      })
    };
  });
}
const Z = () => {
  const e = V(), t = Le();
  return {
    optionGroups: f(
      () => Ge(
        e.optionGroups,
        e.variants,
        e.selectedOptions
      ),
      [
        e.optionGroups,
        e.variants,
        e.selectedOptions
      ]
    ),
    selectedOptions: e.selectedOptions,
    selectOption: (o, s) => t({
      type: "SELECT_OPTION",
      payload: { dimensionKey: o, option: s }
    }),
    clearOptions: () => t({
      type: "CLEAR_OPTIONS"
    })
  };
}, Ae = () => {
  const e = Z();
  return F().filter(
    ({ dimensionKey: o }) => !e.selectedOptions[o]
  );
}, Re = () => {
  const e = S();
  if (!Ae().length)
    return e.variantId;
}, H = () => V().variants, J = h(void 0), x = (e) => /* @__PURE__ */ c(J.Provider, { value: e.purchaseInfo, children: e.children }), Q = h(void 0), X = (e) => /* @__PURE__ */ c(Q.Provider, { value: e.purchaseStatus, children: e.children }), _e = {
  strictVariantMatching: !1
}, Y = h(_e), Ne = () => l(Y), Ue = (e) => {
  const t = m(), r = S(), o = Re(), s = F(), { strictVariantMatching: i } = Ne(), { selectedOptions: d } = Z(), a = H(), u = {
    sku: r.sku,
    priceInfo: r.priceInfo,
    inventory: r.inventory,
    additionalInventory: r.additionalInventory
  }, p = i ? q(a, d) !== void 0 : !0;
  let b;
  return t.discontinued && !r.inventory.inStock && s.length === 0 ? b = { status: "discontinued" } : o ? r.inventory.inStock && p ? b = {
    status: "available",
    availableItemId: o
  } : b = {
    status: "out-of-stock",
    // Default to variantId if outOfStockItemId is not set
    outOfStockItemId: r.outOfStockItemId ?? r.variantId
  } : b = {
    status: "not-determined",
    missingDimensions: s
  }, /* @__PURE__ */ c(x, { purchaseInfo: u, children: /* @__PURE__ */ c(X, { purchaseStatus: b, children: e.children }) });
}, $e = () => {
  const e = ze(), t = We();
  return f(() => {
    if (!(!t || !e))
      return {
        ...e,
        productData: {
          ...e.productData,
          variant: t
        }
      };
  }, [e == null ? void 0 : e.productData.base.productId, t == null ? void 0 : t.variantId]);
}, ze = () => {
  try {
    return M();
  } catch {
    return;
  }
}, We = () => {
  try {
    return S();
  } catch {
    return;
  }
};
function qe(e, t) {
  const r = g(!0);
  y(() => {
    if (r.current) {
      r.current = !1;
      return;
    }
    return e();
  }, t);
}
const Fe = () => {
  const e = $e();
  return qe(() => {
    e && E.emit("productVariantSelected", e);
  }, [
    e,
    e == null ? void 0 : e.productData.base.productId,
    e == null ? void 0 : e.productData.variant.variantId
  ]), null;
}, C = h(void 0), ee = h(void 0), Ze = (e) => {
  const [t, r] = L(
    Ke,
    e.variantState
  ), o = g(!0);
  return y(() => {
    o.current ? o.current = !1 : e.variantState.variants !== t.variants && r({
      type: "SET_STATE",
      payload: e.variantState
    });
  }, [e.variantState]), /* @__PURE__ */ c(C.Provider, { value: t, children: /* @__PURE__ */ c(ee.Provider, { value: r, children: /* @__PURE__ */ K(Ue, { children: [
    /* @__PURE__ */ c(Fe, {}),
    e.children
  ] }) }) });
};
function He(e) {
  const t = {};
  return e.forEach((r) => {
    r.dimensions.forEach((o) => {
      const s = t[o.dimensionKey], i = (s == null ? void 0 : s.options) ?? [], d = i.find(
        (u) => u.dimensionValue === o.value
      );
      let a;
      d ? a = i.map(
        (u) => u.dimensionValue === o.value && !u.inventory.inStock ? {
          ...u,
          inventory: {
            ...u.inventory,
            inStock: r.inventory.inStock
          }
        } : u
      ) : a = [
        ...i,
        {
          dimensionValue: o.value,
          label: o.valueLabel,
          inventory: r.inventory,
          priceInfo: r.priceInfo
        }
      ], t[o.dimensionKey] = {
        dimensionKey: o.dimensionKey,
        dimensionLabel: o.dimensionLabel,
        options: a
      };
    });
  }), Object.values(t);
}
const Je = (e) => {
  const { optionGroups: t, selectedOptions: r } = f(() => {
    const i = He(e.product.variants), d = Object.fromEntries(
      i.map((a) => [a.dimensionKey, void 0])
    );
    return { optionGroups: i, selectedOptions: d };
  }, [e.product.variants]), o = e.product.variants[0];
  if (!o)
    return console.error(
      "Product must have at least one variant, check your mapper for bugs"
    ), null;
  const s = {
    variants: e.product.variants,
    selectedVariant: o,
    initialProductVariant: o,
    selectedOptions: r,
    optionGroups: t
  };
  return /* @__PURE__ */ c(Ze, { variantState: s, children: e.children });
}, te = () => {
  const e = l(re);
  if (!e)
    throw new Error("useBundleProduct must be used within a ProductProvider");
  return e;
}, Qe = () => te().bundlePurchaseInfo, I = () => {
  const e = l(oe);
  if (!e)
    throw new Error("useBundleState must be used within a BundleProvider");
  return e;
}, ne = h(void 0), Xe = (e) => /* @__PURE__ */ c(ne.Provider, { value: e.purchaseStatus, children: e.children }), Ye = ({
  children: e
}) => {
  const t = m(), r = Qe(), o = I(), s = {
    sku: r.sku,
    priceInfo: r.priceInfo,
    inventory: r.inventory,
    additionalInventory: r.additionalInventory
  }, i = Object.values(o.sections).map((u) => ({
    sectionId: u.sectionId,
    availableItemId: u.activeProduct.itemId
  })), d = i.every(
    (u) => typeof u.availableItemId == "string"
  );
  let a;
  return t.discontinued ? a = { status: "discontinued" } : r.inventory.inStock ? d ? a = {
    status: "available",
    sections: i
  } : a = {
    status: "not-determined"
  } : a = {
    status: "out-of-stock"
  }, /* @__PURE__ */ c(x, { purchaseInfo: s, children: /* @__PURE__ */ c(Xe, { purchaseStatus: a, children: e }) });
}, et = (e, t) => {
  const r = e.sections[t.payload.sectionId];
  if (!r)
    throw new Error(`Section '${t.payload.sectionId}' not found`);
  return {
    ...e,
    sections: {
      ...e.sections,
      [t.payload.sectionId]: {
        ...r,
        activeProduct: {
          ...r.activeProduct,
          itemId: t.payload.itemId
        }
      }
    }
  };
}, tt = (e, t) => {
  const r = e.sections[t.payload.sectionId];
  if (!r)
    throw new Error(`Section '${t.payload.sectionId}' not found`);
  if (!r.products.includes(t.payload.product))
    throw new Error(
      `Product '${t.payload.product.base.content.name}' not found in section '${t.payload.sectionId}'`
    );
  return {
    ...e,
    sections: {
      ...e.sections,
      [t.payload.sectionId]: {
        ...r,
        activeProduct: {
          product: t.payload.product,
          itemId: void 0
        }
      }
    }
  };
};
function nt(e, t) {
  switch (t.type) {
    case "SELECT_SECTION_PRODUCT":
      return tt(e, t);
    case "SELECT_BUNDLE_PRODUCT_SIZE":
      return et(e, t);
    default:
      throw new Error(`Invalid action type '${t == null ? void 0 : t.type}'`);
  }
}
const re = h(
  void 0
), oe = h(
  void 0
), ie = h(void 0), rt = ({
  children: e,
  product: t
}) => {
  const r = {};
  for (const i of t.sections) {
    const d = i.products[0];
    if (!d) {
      console.error(
        `Section '${i.sectionId}' does not have any products`
      );
      continue;
    }
    r[i.sectionId] = {
      sectionId: i.sectionId,
      products: i.products,
      activeProduct: { product: d, itemId: void 0 }
    };
  }
  const [o, s] = L(nt, {
    sections: r
  });
  return /* @__PURE__ */ c(re.Provider, { value: t, children: /* @__PURE__ */ c(oe.Provider, { value: o, children: /* @__PURE__ */ c(ie.Provider, { value: s, children: /* @__PURE__ */ c(Ye, { children: e }) }) }) });
}, se = h(!1), ot = (e) => /* @__PURE__ */ c(se.Provider, { value: e.isBundle, children: e.children }), Dt = () => l(se), it = (e) => {
  const t = {
    sku: e.product.sku,
    priceInfo: e.product.priceInfo,
    inventory: e.product.inventory,
    additionalInventory: e.product.additionalInventory
  };
  let r;
  return e.product.inventory.inStock ? r = {
    status: "available",
    availableItemId: e.product.itemId
  } : e.product.base.discontinued ? r = {
    status: "discontinued"
  } : r = {
    status: "out-of-stock",
    // Default to itemId if outOfStockItemId is not set
    outOfStockItemId: e.product.outOfStockItemId ?? e.product.itemId
  }, /* @__PURE__ */ c(x, { purchaseInfo: t, children: /* @__PURE__ */ c(X, { purchaseStatus: r, children: /* @__PURE__ */ c(ot, { isBundle: e.product.isBundle, children: e.children }) }) });
}, ce = h(void 0), st = () => {
  const e = l(ce);
  if (!e)
    throw new Error(
      "useProductProviderContext must be used within a ProductProvider"
    );
  return e;
}, T = (e) => {
  const { fireOnMount: t = !1 } = e, r = M(), o = g(null), s = ve({ refElement: o });
  return y(() => {
    (t || s) && E.emit("productViewed", r);
  }, [r, t, s]), /* @__PURE__ */ K("div", { style: { position: "relative" }, children: [
    /* @__PURE__ */ c(
      "div",
      {
        ref: o,
        style: {
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: -1
        }
      }
    ),
    e.children
  ] });
}, de = h(void 0), ct = () => l(de) ?? {}, dt = n.object({
  children: n.custom(),
  strictVariantMatching: n.boolean().optional().describe(
    "Global default for strictVariantMatching. When true, prevents adding invalid size combinations to cart across all product lists and cards. Can be overridden per-component."
  )
}), kt = G(
  dt,
  ({ children: e, strictVariantMatching: t }) => {
    const r = {
      strictVariantMatching: t
    };
    return /* @__PURE__ */ c(de.Provider, { value: r, children: e });
  }
), ae = h(
  void 0
), ue = h(void 0), le = h(
  []
), he = h(
  []
), at = je.extend({
  product: n.custom(),
  relatedProducts: W.array().optional(),
  children: n.custom(),
  context: n.custom(),
  fireOnMount: n.boolean().optional()
}), ut = G(
  at,
  (e) => {
    const t = ct(), r = e.relatedProducts ?? [], o = f(() => {
      const d = e.product.siblingProducts ?? [];
      d.find(
        (u) => u.productId === e.product.base.productId
      ) === void 0 && d.push(e.product.base);
      const a = JSON.parse(
        JSON.stringify(d)
      );
      return a.sort((u, p) => u.productId.localeCompare(p.productId)), a;
    }, [e.product.base, e.product.siblingProducts]), s = f(
      () => ({
        strictVariantMatching: t.strictVariantMatching ?? !1
      }),
      [t.strictVariantMatching]
    );
    let i;
    switch (e.product.dataModel) {
      case "simple":
        i = /* @__PURE__ */ c(it, { product: e.product, children: /* @__PURE__ */ c(T, { fireOnMount: e.fireOnMount, children: e.children }) });
        break;
      case "with_variants":
        i = /* @__PURE__ */ c(Je, { product: e.product, children: /* @__PURE__ */ c(T, { fireOnMount: e.fireOnMount, children: e.children }) });
        break;
      case "bundle":
        i = /* @__PURE__ */ c(rt, { product: e.product, children: /* @__PURE__ */ c(T, { fireOnMount: e.fireOnMount, children: e.children }) });
        break;
      default:
        return console.error(
          // @ts-expect-error This is a runtime check, so we print dataModel even if TS says it doesn't exist
          `Unknown product data model: "${e.product.dataModel}", check your mapper for bugs`
        ), null;
    }
    return /* @__PURE__ */ c(Y.Provider, { value: s, children: /* @__PURE__ */ c(ce.Provider, { value: e.context, children: /* @__PURE__ */ c(ue.Provider, { value: e.product.dataModel, children: /* @__PURE__ */ c(ae.Provider, { value: e.product.base, children: /* @__PURE__ */ c(le.Provider, { value: o, children: /* @__PURE__ */ c(he.Provider, { value: r, children: i }) }) }) }) }) });
  }
);
ut.displayName = "ProductProvider";
const m = () => {
  const e = l(ae);
  if (!e)
    throw new Error("useProductBase must be used within a ProductProvider");
  return e;
}, B = () => {
  const e = l(J);
  if (!e)
    throw new Error(
      "useProductPurchaseInfo must be used within a ProductPurchaseInfoProvider"
    );
  return e;
}, M = () => {
  const e = m(), t = B(), r = st(), { additionalData: o } = Pe();
  return f(
    () => ({
      productData: {
        base: e,
        purchaseInfo: t
      },
      context: r,
      additionalData: o
    }),
    [e.productId]
  );
}, Vt = (e) => {
  const t = pe(), r = M(), o = g({
    ...r,
    productData: { ...r.productData, base: e.productBase }
  });
  y(() => {
    o.current = {
      ...r,
      productData: { ...r.productData, base: e.productBase }
    };
  }, [r, e.productBase]);
  const s = me(() => {
    E.emit("productClicked", o.current), fe.emit("close", {});
  }, []), { productBase: i, ...d } = e;
  return /* @__PURE__ */ c(
    t,
    {
      ...d,
      onClick: s,
      href: e.productBase.slug
    }
  );
}, xt = () => {
  try {
    return m();
  } catch {
    return;
  }
}, Ct = ({
  attributes: e
}) => (t, r) => {
  if (!t || !e) {
    console.error("Attribute key is not set");
    return;
  }
  const o = e.find(
    (i) => i.attributeKey === t
  );
  if (!(typeof o > "u" || !r.safeParse(o.value).success))
    return o;
}, lt = ({
  attributes: e
}) => (t, r) => ht(e, t, r), ht = (e, t, r) => {
  if (!t) {
    console.error("Attribute key is not set");
    return;
  }
  const o = e.find(
    (i) => i.attributeKey === t
  );
  if (!(typeof o > "u" || !r.safeParse(o.value).success))
    return o;
}, Bt = (e, t) => {
  const r = m();
  return lt(r)(e, t);
}, Mt = () => {
  const e = l(ue);
  if (!e)
    throw new Error(
      "useProductDataModel must be used within a ProductProvider"
    );
  return e;
}, jt = () => V().optionGroups.some(
  (t) => t.options.some((r) => r.inventory.inStock)
), pt = (e, t) => e.filter((r) => {
  var s;
  const o = (s = r.attributes) == null ? void 0 : s.find(
    (i) => i.attributeKey === t.key
  );
  return t.value.includes(o == null ? void 0 : o.value);
}), Kt = (e) => {
  const {
    content: { media: t }
  } = m(), r = t.filter(Ie), o = e == null ? void 0 : e.attributeFilter;
  return o === void 0 ? r : pt(
    r,
    o
  );
}, Lt = () => B().inventory, Gt = () => H().some((t) => t.inventory.inStock), At = () => B().priceInfo, Rt = () => {
  const e = l(Q);
  if (!e)
    throw new Error(
      "useProductPurchaseStatus must be used within a ProductPurchaseStatusProvider"
    );
  return e;
}, _t = () => {
  const e = l(le);
  if (!e)
    throw new Error("useProductSiblings must be used within a ProductProvider");
  return e;
}, Nt = (e) => {
  var r;
  return (r = m().content.specifications) == null ? void 0 : r[e];
}, Ut = (e) => {
  const t = l(he);
  if (!t)
    throw new Error("useRelatedProducts must be used within a ProductProvider");
  return e ? t.filter(
    (r) => r.relationType === e
  ) : t;
}, bt = () => {
  const e = l(ie);
  if (!e)
    throw new Error("useBundleDispatch must be used within a BundleProvider");
  return e;
}, $t = () => te().itemId, zt = () => {
  const e = I(), t = bt(), r = (s, i) => {
    t({
      type: "SELECT_BUNDLE_PRODUCT_SIZE",
      payload: {
        sectionId: s,
        itemId: i
      }
    });
  }, o = (s, i) => {
    t({
      type: "SELECT_SECTION_PRODUCT",
      payload: {
        sectionId: s,
        product: i
      }
    });
  };
  return {
    sections: e.sections,
    selectSectionSize: r,
    selectSectionProduct: o
  };
}, Wt = () => {
  const e = l(ne);
  if (!e)
    throw new Error(
      "useBundlePurchaseStatus must be used within a BundlePurchaseStatusProvider"
    );
  return e;
}, mt = (e) => {
  const { sections: t } = I(), r = t[e];
  if (!r)
    throw new Error(`Section '${e}' not found`);
  return r.activeProduct.product;
}, qt = (e) => {
  const { sections: t } = I(), r = mt(e), o = t[e];
  if (!o)
    throw new Error(`Section '${e}' not found`);
  return o.products.filter((s) => s !== r);
}, Ft = n.object({
  siblings: P.array().describe("The sibling products"),
  selectedSibling: P.describe("The selected sibling product")
}), Zt = n.object({
  label: n.string(),
  value: n.string(),
  icon: n.custom(() => !0).optional()
}), vt = n.object({
  label: n.string().describe("The label of the option, like 'Red' or 'Small'"),
  dimensionValue: n.string().describe("The value of the dimension, used when doing add to cart"),
  inventory: w,
  priceInfo: N.describe(
    "The price information for this option, useful in case of a price change depending on a selected option, like different materials"
  )
}), Ht = n.object({
  dimensionKey: n.string().describe("The dimension key"),
  dimensionLabel: n.string().describe("The dimension label"),
  options: n.array(vt)
});
export {
  rt as BundleProductProvider,
  Xe as BundlePurchaseStatusProvider,
  kt as GlobalProductSettingsProvider,
  ot as IsBundleProvider,
  Ye as ProductBundlePurchaseProvider,
  Ot as ProductEventAdditionalDataProvider,
  Vt as ProductLink,
  ut as ProductProvider,
  x as ProductPurchaseInfoProvider,
  X as ProductPurchaseStatusProvider,
  it as ProductSimpleProvider,
  Fe as ProductVariantEventDispatcher,
  Ze as ProductVariantProvider,
  Ue as ProductVariantPurchaseProvider,
  T as ProductViewedEvent,
  Je as ProductWithVariantsProvider,
  O as attributeSchema,
  ie as bundleDispatchContext,
  re as bundleProductContext,
  Be as bundlePurchaseInfoSchema,
  ne as bundlePurchaseStatusContext,
  nt as bundleReducer,
  oe as bundleStateContext,
  pt as filterMediaOnAttributeValues,
  ht as getProductAttribute,
  q as getVariantFromOptions,
  se as isBundleContext,
  Ie as isProductMediaImage,
  ke as previouslyDiscountedPriceSchema,
  _ as priceInfoSchema,
  Te as productBadgeSchema,
  ae as productBaseContext,
  P as productBaseSchema,
  Me as productBundleSchema,
  Ce as productBundleSectionSchema,
  Ee as productCategorySchema,
  we as productContentSchema,
  ue as productDataModelContext,
  E as productEventEmitter,
  R as productMediaDimensionsSchema,
  ge as productMediaImageSchema,
  Se as productMediaSchema,
  ye as productMediaVideoSchema,
  Ht as productOptionGroupSchema,
  vt as productOptionSchema,
  ce as productProviderContextContext,
  at as productProviderSchema,
  J as productPurchaseInfoContext,
  k as productPurchaseInfoSchema,
  w as productPurchaseInventorySchema,
  De as productPurchasePriceDiscountSchema,
  N as productPurchasePriceSchema,
  Q as productPurchaseStatusContext,
  he as productRelatedProductsContext,
  z as productSchema,
  Y as productSettingsContext,
  D as productSharedSchema,
  le as productSiblingProductsContext,
  Oe as productSiblingSchema,
  Ft as productSiblingsSchema,
  U as productSimpleSchema,
  Zt as productSpecificationSchema,
  Ve as productVariantDimensionSchema,
  ee as productVariantDispatchContext,
  Ke as productVariantReducer,
  xe as productVariantSchema,
  C as productVariantStateContext,
  je as productViewModelSchema,
  $ as productWithVariantsSchema,
  W as relatedProductSchema,
  et as selectBundleProductSize,
  tt as selectSectionProduct,
  bt as useBundleDispatch,
  $t as useBundleItemId,
  zt as useBundleOptions,
  te as useBundleProduct,
  Qe as useBundlePurchaseInfo,
  Wt as useBundlePurchaseStatus,
  mt as useBundleSectionActiveProduct,
  qt as useBundleSectionProductAlternatives,
  I as useBundleState,
  ct as useGlobalProductSettings,
  Dt as useIsBundle,
  xt as useMaybeProductBase,
  Ct as useMediaAttributeGetter,
  Bt as useProductAttribute,
  lt as useProductAttributeGetter,
  m as useProductBase,
  Mt as useProductDataModel,
  Pe as useProductEventAdditionalData,
  M as useProductEventData,
  jt as useProductHasOptionsInStock,
  Kt as useProductImages,
  Lt as useProductInventory,
  Gt as useProductIsInStock,
  Z as useProductOptions,
  At as useProductPriceInfo,
  st as useProductProviderContext,
  B as useProductPurchaseInfo,
  Rt as useProductPurchaseStatus,
  Ne as useProductSettings,
  _t as useProductSiblings,
  Nt as useProductSpecificationGroup,
  S as useProductVariant,
  F as useProductVariantDimensions,
  Le as useProductVariantDispatch,
  $e as useProductVariantEventData,
  V as useProductVariantState,
  H as useProductVariants,
  Ut as useRelatedProducts,
  Re as useSelectedVariantId,
  Ae as useVariantMissingDimensions,
  He as variantsToProductOptionGroups
};
